<template>
  <section class="page-hero dark">
    <div class="container">
      <div class="row">
        <div class="col-12 text-center">
          <nav v-if="crumbs.length" class="page-crumbs" aria-label="Breadcrumb">
            <template v-for="(crumb, index) in crumbs" :key="crumb.label">
              <span v-if="index" class="page-crumb-sep" aria-hidden="true">/</span>
              <Link
                v-if="crumb.href"
                :href="crumb.href"
                class="page-crumb"
              >
                {{ crumb.label }}
              </Link>
              <span
                v-else
                class="page-crumb"
                :class="{ 'is-current': index === crumbs.length - 1 }"
                :aria-current="index === crumbs.length - 1 ? 'page' : null"
              >{{ crumb.label }}</span>
            </template>
          </nav>

          <h1 class="page-title">{{ title }}</h1>
          <a
            v-if="link"
            class="page-link"
            :href="link"
            target="_blank"
            rel="noopener"
          >{{ link }}</a>
          <p v-if="lede" class="page-lede">{{ lede }}</p>
          <p v-if="meta" class="page-meta">{{ meta }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.page-hero {
  background: $grad-cta;
  color: $on-dark-strong;
  padding: 10rem 0 4rem;
  position: relative;

  &::before {
    background: url('/images/home/hero/bg.png') no-repeat top;
    background-size: cover;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  /* The crumb trail carries the shared $label-* spec: it is the same small
     uppercase Rubik label as a section kicker, and it had spelled the same
     size and tracking out by hand. Its colour stays its own, because the hero
     is the blue gradient and the trail has to hold contrast against it: muted
     for the trail, white for the page you are on. */
  .page-crumbs {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    font-family: $headings-font-family;
    font-size: $label-size;
    font-weight: $label-weight;
    gap: 0.5rem;
    justify-content: center;
    letter-spacing: $label-tracking;
    margin-bottom: 1.15rem;
    text-transform: uppercase;
  }

  .page-crumb {
    color: $on-dark-body;
    text-decoration: none;
    transition: color $dur-quick $ease;

    &:hover,
    &:focus {
      color: $on-dark-strong;
    }

    &.is-current {
      color: $on-dark-strong;
    }
  }

  .page-crumb-sep {
    opacity: 0.5;
  }

  /* The title owns its own measure rather than inheriting one from a grid
     column. A column is a percentage of a container that changes at every
     breakpoint, minus a 48px gutter (this project sets $grid-gutter-width to
     3rem, so the gutter is double Bootstrap's 24px default), which puts a
     9-column box on 636px at lg, 807px at xl and 906px at xxl. Three
     viewports, three accidental measures. One number cannot drift.

     The cap only ever narrows, so it holds at every breakpoint: 1000px once
     the container is wide enough, and the full container below that. The lede
     keeps its own 38rem measure underneath, so only the title widens. */
  .page-title {
    color: $on-dark-strong;
    font-size: clamp(2.4rem, 5vw, 3.6rem);
    line-height: 1.1;
    margin: 0 auto 1rem;
    max-width: 1000px;
  }

  .page-lede {
    color: $on-dark-body;
    font-size: 1rem;
    line-height: 1.5;
    margin: 0 auto;
    max-width: 38rem;
  }

  /* An optional external link under the title, for the one page whose subject is
     a URL: the website review, where the title names the business and this is the
     address being reviewed. It sits with the title rather than the lede so that
     address reads as part of the heading, and it is text rather than a button
     because the hero may not carry an action row. */
  .page-link {
    color: $on-dark-body;
    display: inline-block;
    font-size: 0.95rem;
    margin-bottom: 1rem;
    text-decoration: none;
    transition: color $dur-quick $ease;
    word-break: break-all;

    &:hover,
    &:focus {
      color: $on-dark-strong;
    }
  }

  /* Meta line for pages that carry a date, author or report stamp. The only
     thing a page may put in the hero besides the crumbs, title and lede, and
     it arrives as a prop so the hero cannot be filled with anything else. */
  .page-meta {
    color: $on-dark-body;
    display: flex;
    flex-wrap: wrap;
    font-size: 0.9rem;
    gap: 0.5rem;
    justify-content: center;
    margin-top: 1rem;
  }
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';

export default {
  components: {
    Link
  },

  props: {
    title: {
      type: String,
      required: true
    },
    lede: {
      type: String,
      default: ''
    },
    /* Optional meta line under the lede, for a date, author or report stamp.
       This replaced a default slot, which let any page put arbitrary content
       in the hero and drift away from the shared chrome. */
    meta: {
      type: String,
      default: ''
    },
    /* Optional external URL rendered under the title. Added for the website
       review, whose subject is a URL; it is a prop rather than a slot so the
       hero still cannot be filled with arbitrary content. */
    link: {
      type: String,
      default: ''
    },
    crumbs: {
      type: Array,
      default: () => []
    }
  }
};
</script>
